import type { ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useOriginate } from "@/components/agent/phone/useOriginate";
import {
  MailIcon,
  MobileIcon,
  PhoneIncomingIcon,
  PhoneOutgoingIcon,
} from "@/components/ui/icons";
import { cn } from "@/lib/cn";
import { actionClass, iconActionClass } from "./customerFormat";
import { dialHref } from "@/lib/phoneCall";

type Customer = {
  login: string;
  first_name?: string | null;
  last_name?: string | null;
};

const displayName = (c: Customer) =>
  [c.first_name, c.last_name].filter(Boolean).join(" ").trim() || c.login;

/** A contact row of the customer card: what it is, what a click does, the value. */
export const contactRowClass =
  "flex w-full min-h-[56px] items-center gap-3 rounded-lg border border-hairline bg-surface px-3 py-2 text-left transition-colors duration-100 hover:border-muted/40 hover:bg-surface-subtle focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent";

function ContactRowBody({
  icon,
  label,
  verb,
  value,
  tone,
}: {
  icon: ReactNode;
  label: string;
  verb: string;
  value: string;
  tone: "call" | "mail";
}) {
  return (
    <>
      <span
        className={cn(
          "grid h-8 w-8 shrink-0 place-items-center rounded-md",
          tone === "call" ? "bg-green/10 text-green" : "bg-accent-dim text-accent",
        )}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center justify-between gap-2 text-xs">
          <span className="text-muted">{label}</span>
          <span className="font-semibold text-ink">{verb}</span>
        </span>
        <span className="block text-[15px] font-medium tabular-nums text-ink [overflow-wrap:anywhere]">
          {value}
        </span>
      </span>
    </>
  );
}

/**
 * "New e-mail ticket" for this customer (the new-ticket form, prefilled).
 * `email` turns it into the card's contact row: the address that, like a
 * number, starts the conversation inside Tiqora.
 */
export function EmailTicketLink({
  login,
  compact,
  primary,
  email,
}: {
  login: string;
  compact?: boolean;
  primary?: boolean;
  email?: string;
}) {
  const { t } = useTranslation();
  const label = t("customerWorkbench.emailTicket");
  return (
    <Link
      to="/agent/tickets/new"
      search={{ type: "email", customer: login }}
      className={email ? contactRowClass : compact ? iconActionClass : actionClass(primary)}
      aria-label={compact ? label : undefined}
      title={compact || email ? label : undefined}
      data-testid={`customer-email-ticket-${login}`}
    >
      {email ? (
        <ContactRowBody
          icon={<MailIcon className="text-[16px]" />}
          label={t("customerCentre.email")}
          verb={label}
          value={email}
          tone="mail"
        />
      ) : (
        <>
          <MailIcon className="text-[15px]" />
          {!compact && label}
        </>
      )}
    </Link>
  );
}

/** "Log a call from them": an inbound phone ticket, no dialling. */
export function PhoneTicketLink({ login, className }: { login: string; className?: string }) {
  const { t } = useTranslation();
  return (
    <Link
      to="/agent/tickets/new"
      search={{ type: "phone", direction: "inbound", customer: login }}
      className={cn(actionClass(), "justify-center", className)}
      data-testid={`customer-phone-ticket-${login}`}
    >
      <PhoneIncomingIcon className="text-[15px] text-channel-phone" />
      {t("customerWorkbench.logInboundCall")}
    </Link>
  );
}

/**
 * Call a number. With click-to-dial configured the agent's desk phone rings
 * first; once the PBX took the call the outbound phone ticket opens (the same
 * flow as `DialLink`). Without it, a `tel:`/`sip:` link that opens the form
 * right away. Ctrl/Cmd-click always follows the plain link.
 */
export function CallAction({
  customer,
  number,
  kind = "phone",
  compact,
  row,
}: {
  customer: Customer;
  number: string;
  kind?: "phone" | "mobile";
  compact?: boolean;
  /** A contact row of the customer card. */
  row?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enabled, scheme, status, dial } = useOriginate();
  const openForm = () =>
    void navigate({
      to: "/agent/tickets/new",
      search: { type: "phone", direction: "outbound", customer: customer.login, number },
    });
  const label = t("customerWorkbench.callNumber", { number });
  const icon: ReactNode =
    kind === "mobile" ? (
      <MobileIcon className="text-[15px] text-green" />
    ) : (
      <PhoneOutgoingIcon className="text-[15px] text-green" />
    );
  return (
    <span className={cn("flex-col", row ? "flex" : "inline-flex")}>
      <a
        href={dialHref(number, scheme)}
        onClick={(e) => {
          if (enabled && !e.metaKey && !e.ctrlKey) {
            e.preventDefault();
            void dial(number, { name: displayName(customer) }).then((ok) => {
              if (ok) openForm();
            });
            return;
          }
          openForm();
        }}
        className={compact ? iconActionClass : row ? contactRowClass : cn(actionClass(), "tabular-nums")}
        aria-label={compact ? label : undefined}
        title={compact ? label : t("customerWorkbench.callHint")}
        data-testid={`customer-call-${kind}-${customer.login}`}
      >
        {row ? (
          <ContactRowBody
            icon={kind === "mobile" ? <MobileIcon className="text-[16px]" /> : <PhoneOutgoingIcon className="text-[16px]" />}
            label={kind === "mobile" ? t("customerWorkbench.mobile") : t("customerWorkbench.phone")}
            verb={t("customerWorkbench.callVerb")}
            value={number}
            tone="call"
          />
        ) : (
          <>
            {icon}
            {!compact && number}
          </>
        )}
      </a>
      {!compact && status.kind === "ringing" && (
        <span className="mt-0.5 text-xs text-muted">
          {t("phone.dialRinging", { extension: status.extension })}
        </span>
      )}
      {status.kind === "error" && (
        <span className="mt-0.5 text-xs text-danger" role="alert">
          {status.message}
        </span>
      )}
    </span>
  );
}
